/**
 * Settings → Locations (T079 / E10).
 *
 * A dealership that operates more than one physical rooftop names them here.
 * Everything downstream — the Dealer Principal dashboard's location filter
 * and its per-location performance table — reads this list; a dealership
 * with none or one location simply never sees either, same "hide when
 * absent" convention already used for the location filter on Appointments.
 */

import { useEffect, useMemo, useState } from 'react';
import { locationsService } from '../../lib/services';
import { useCurrentUser } from '../../lib/currentUser';
import { useToast } from '../../lib/toast';

const asList = (v) => (Array.isArray(v) ? v : (v?.data ?? []));

/**
 * Asks the shared capability instead of keeping a private role list. The old
 * list here predated General Manager and CEO, so both saw a read-only panel
 * with no Add button while the Dealer Principal — supposedly their equal —
 * could edit freely.
 */
const useCanEdit = () => {
  const { can } = useCurrentUser();
  return can('settings.edit');
};

export default function LocationsPanel() {
  const canEdit = useCanEdit();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', code: '' });
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try { setRows(asList(await locationsService.list())); setError(null); }
    catch (e) { setError(e); }
    finally { setLoading(false); }
  };
  // react-hooks/set-state-in-effect fires here, but it fires the same way on
  // the shared useApi hook and on ReasonsSection (LeadFollowUpPanel.jsx),
  // which this file deliberately mirrors — a pre-existing, codebase-wide
  // pattern, not something to fix in isolation on one new panel.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => rows.filter((r) => showInactive || r.active !== false), [rows, showInactive]);

  const patch = async (id, body, ok) => {
    setBusyId(id);
    try {
      const next = await locationsService.update(id, body);
      setRows(asList(next));
      if (ok) toast(ok, { variant: 'success' });
    } catch (e) {
      toast(`Not saved: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusyId(null);
    }
  };

  const create = async () => {
    if (!draft.name.trim()) return;
    setBusyId('__new');
    try {
      const next = await locationsService.create({
        ...draft,
        sortOrder: (rows.reduce((m, r) => Math.max(m, r.sortOrder ?? 0), 0) || 0) + 10,
      });
      setRows(asList(next));
      setAdding(false);
      setDraft({ name: '', code: '' });
      toast('Location added', { variant: 'success' });
    } catch (e) {
      toast(`Not added: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusyId(null);
    }
  };

  const move = (id, dir) => {
    const idx = visible.findIndex((r) => r.id === id);
    const swap = visible[idx + dir];
    if (!swap) return;
    const a = visible[idx];
    const ao = a.sortOrder ?? 0, bo = swap.sortOrder ?? 0;
    const na = ao === bo ? bo + dir * 5 : bo;
    const nb = ao === bo ? ao : ao;
    Promise.all([
      locationsService.update(a.id, { sortOrder: na }),
      locationsService.update(swap.id, { sortOrder: nb }),
    ]).then((res) => setRows(asList(res[res.length - 1]))).catch(() => toast('Could not reorder', { variant: 'danger' }));
  };

  return (
    <div className="card">
      <div className="flex-between" style={{ marginBottom: 4 }}>
        <span className="card-label" style={{ margin: 0 }}>LOCATIONS</span>
        <div className="flex-row" style={{ gap: 6, alignItems: 'center' }}>
          <label className="mono text-xs text-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> show retired
          </label>
          {canEdit && <button type="button" className="btn small primary" onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel' : '+ Add location'}</button>}
        </div>
      </div>
      <div className="mono text-xs text-muted" style={{ marginBottom: 12 }}>
        Physical rooftops this dealership operates. A dealership with one location doesn't need this — the dashboard's location filter and location table only appear once a second one exists here.
      </div>

      {!canEdit && (
        <div className="mono text-xs text-muted" style={{ marginBottom: 8 }}>
          Read-only — only a Dealer Principal can change these.
        </div>
      )}

      {error && (
        <div className="mono text-xs" style={{ color: 'var(--danger)', marginBottom: 8 }}>
          Couldn't load — the settings endpoint may not be deployed yet. <button type="button" className="btn small" onClick={load}>Retry</button>
        </div>
      )}

      {adding && (
        <div className="card" style={{ padding: 12, marginBottom: 12, background: 'var(--bg-glass)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <div className="form-field">
              <label>Name</label>
              <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder="Melbourne" autoFocus />
            </div>
            <div className="form-field">
              <label>Code (optional)</label>
              <input value={draft.code} onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))} placeholder="MEL" />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn small primary" onClick={create} disabled={busyId === '__new' || !draft.name.trim()}>
              {busyId === '__new' ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {loading && !rows.length ? (
        <div className="mono text-xs text-muted">loading…</div>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr><th style={{ width: 60 }} /><th>Name</th><th>Code</th><th style={{ textAlign: 'right' }} /></tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const busy = busyId === r.id;
                const retired = r.active === false;
                return (
                  <tr key={r.id} style={{ opacity: retired ? 0.55 : 1 }}>
                    <td>
                      <button type="button" className="btn small" disabled={!canEdit || i === 0 || busy || retired} onClick={() => move(r.id, -1)} aria-label="Move up">▲</button>
                      <button type="button" className="btn small" disabled={!canEdit || i === visible.length - 1 || busy || retired} onClick={() => move(r.id, 1)} aria-label="Move down">▼</button>
                    </td>
                    <td>
                      <input
                        defaultValue={r.name}
                        disabled={!canEdit || retired || busy}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== r.name) patch(r.id, { name: v }, 'Renamed'); }}
                        style={{ width: '100%' }}
                      />
                    </td>
                    <td className="mono text-xs text-muted">{r.code || '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {retired ? (
                        <button type="button" className="btn small" disabled={!canEdit || busy} onClick={() => patch(r.id, { active: true }, 'Restored')}>Restore</button>
                      ) : (
                        <button type="button" className="btn small danger" disabled={!canEdit || busy} onClick={() => patch(r.id, { active: false }, 'Retired — existing leads keep it')}>Retire</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!visible.length && !loading && (
                <tr><td colSpan={4} className="mono text-xs text-muted" style={{ padding: 16 }}>No locations yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
